import { Icon } from '@eventbrite/legacy-eds.icon';
import {
    ChevronDownChunky,
    ChevronLeftChunky,
    ChevronRightChunky,
    ChevronUpChunky,
} from '@eventbrite/legacy-eds.iconography';
import classNames from 'classnames';
import React, { useRef } from 'react';
import { useGlobalNavContext } from '../../../../../state';
import { getHeapDataAttribute, HeapAttributes } from '../../../../../utils/heap-data';
import { DropdownItem } from '../../../../../types';
import { NavLink } from '../../Links';
import styles from './SecondaryDropdown.module.scss';
import { useIntl } from 'react-intl';
import {
    GLOBAL_NAV_COMPACT_MAX_WIDTH_PX,
    GLOBAL_NAV_DESKTOP_MIN_WIDTH_PX,
} from '../../../../../constants/breakpoints';
import { getDropdownItemListKey } from '../../../../../utils/dropdownItemKey';

export type SecondaryDropdownProps = DropdownItem & { nestDepth?: number };

export const SecondaryDropdown = ({
    nestDepth = 0,
    heapIdentifier,
    translatedText,
    dropdownItems,
    textColor,
    path: _path,
    iconType,
    iconPosition,
    className,
    mobileDividerTop,
}: SecondaryDropdownProps) => {
    const titleRef = useRef<HTMLSpanElement>(null);
    const dropdownTriggerRef = useRef<HTMLLIElement>(null);
    const secondaryDropdownRef = useRef<HTMLUListElement>(null);
    const secondaryDropdownButtonRef = useRef<HTMLButtonElement>(null);
    const { Teams, Features, Components, Actions } = HeapAttributes;
    const { formatMessage } = useIntl();
    const { env } = useGlobalNavContext();

    const depth = Math.min(nestDepth, 3);

    const handleTitleClick = () => {
        if (
            typeof window !== 'undefined' &&
            window.matchMedia(`(min-width: ${GLOBAL_NAV_DESKTOP_MIN_WIDTH_PX}px)`).matches
        ) {
            secondaryDropdownButtonRef.current?.focus();
        }
    };

    const handleButtonClick = () => {
        titleRef.current?.focus();
    };

    const isMobileNavViewport = () =>
        typeof window !== 'undefined' &&
        window.matchMedia(`(max-width: ${GLOBAL_NAV_COMPACT_MAX_WIDTH_PX}px)`).matches;

    const pointerDownHandler = (e: React.PointerEvent<HTMLSpanElement>) => {
        if (!isMobileNavViewport()) {
            e.stopPropagation();
            return;
        }

        const li = dropdownTriggerRef.current;
        const title = titleRef.current;
        if (!li || !title) {
            e.stopPropagation();
            return;
        }

        const ae = document.activeElement;
        const nestedRow =
            ae instanceof Element ? ae.closest('[data-globalnav-secondary-row]') : null;

        if (nestedRow && nestedRow !== li && li.contains(nestedRow)) {
            e.preventDefault();
            queueMicrotask(() => title.focus({ preventScroll: true }));
            e.stopPropagation();
            return;
        }

        if (title === document.activeElement) {
            e.preventDefault();
            const parentList = li.parentElement;
            if (parentList instanceof HTMLElement) {
                queueMicrotask(() => parentList.focus({ preventScroll: true }));
            }
        }
        e.stopPropagation();
    };

    return (
        <li
            tabIndex={-1}
            className={classNames(styles.secondaryDropdownTrigger, className)}
            data-nest-depth={depth}
            data-globalnav-secondary-row
            data-globalnav-text={heapIdentifier}
            ref={dropdownTriggerRef}
            {...(mobileDividerTop ? { 'data-globalnav-mobile-divider-top': '' } : {})}
        >
            <span
                ref={titleRef}
                data-heap-id={getHeapDataAttribute({
                    team: Teams.SEO,
                    feature: Features.GlobalNav,
                    component: Components.Dropdown,
                    name: heapIdentifier.replaceAll(' ', '-').toLowerCase(),
                    action: Actions.Click,
                })}
                onClick={handleTitleClick}
                onPointerDown={pointerDownHandler}
                className={styles.titleContainer}
                tabIndex={0}
            >
                <span className={styles.leadingMobileAccordionIcons}>
                    <span className={styles.accordionArrowIcons}>
                        <Icon type={<ChevronUpChunky />} />
                        <Icon type={<ChevronDownChunky />} />
                    </span>
                </span>
                {iconType && iconPosition !== 'right' ? (
                    <span className={styles.titleRowIcon}>
                        <Icon type={iconType} />
                    </span>
                ) : null}
                <span className={styles.primaryLabel} style={{ color: textColor }}>
                    {formatMessage(translatedText)}
                </span>
                {iconType && iconPosition === 'right' ? (
                    <span className={styles.titleRowIcon}>
                        <Icon type={iconType} />
                    </span>
                ) : null}
                <span className={styles.trailingDesktopChevron}>
                    <span className={styles.iconContainer}>
                        <Icon type={<ChevronRightChunky />} size="small" />
                    </span>
                </span>
            </span>
            <ul className={styles.secondaryDropdown} ref={secondaryDropdownRef} tabIndex={-1}>
                <li className={styles.secondaryDropdownBack}>
                    <button
                        type="button"
                        tabIndex={0}
                        onClick={handleButtonClick}
                        ref={secondaryDropdownButtonRef}
                    >
                        <ChevronLeftChunky />
                        <span style={{ color: textColor }}>{formatMessage(translatedText)}</span>
                    </button>
                </li>
                {dropdownItems?.map(
                    (
                        {
                            heapIdentifier: childHeapId,
                            translatedText: childText,
                            textColor: childTextColor,
                            path = '',
                            iconType: childIconType,
                            iconPosition: childIconPosition,
                            className: childClassName,
                            mobileDividerTop: childMobileDividerTop,
                            dropdownItems: childNested,
                        },
                        index,
                    ) =>
                        childNested?.length ? (
                            <SecondaryDropdown
                                key={getDropdownItemListKey(index, childHeapId, path)}
                                nestDepth={nestDepth + 1}
                                heapIdentifier={childHeapId}
                                translatedText={childText}
                                textColor={childTextColor}
                                path={path}
                                iconType={childIconType}
                                iconPosition={childIconPosition}
                                className={childClassName}
                                mobileDividerTop={childMobileDividerTop}
                                dropdownItems={childNested}
                            />
                        ) : (
                            <li
                                key={getDropdownItemListKey(index, childHeapId, path)}
                                className={styles.navLinkContainer}
                            >
                                <NavLink
                                    path={new URL(path, env?.serverUrl ?? '').href}
                                    heapIdentifier={childHeapId}
                                    translatedText={childText}
                                    textColor={childTextColor}
                                    iconType={childIconType}
                                    iconPosition={childIconPosition}
                                    className={childClassName ?? styles.navLinkDropdown}
                                />
                            </li>
                        ),
                )}
            </ul>
        </li>
    );
};
